import { AnimatePresence, motion } from 'framer-motion';
import { Coffee, Heart, X } from 'lucide-react';
import Button from '@components/ui/Button';
import Icon from '@components/ui/Icon';
import { useUIStore } from '@stores/useUIStore';
import { track } from '@utils/analytics';

const KOFI_URL = 'https://ko-fi.com/studioshell';

/**
 * DonationBanner — non-intrusive slide-up prompt at the bottom of the
 * sidebar. Appears ~2.5s after the user first reaches Step 3 (analysis).
 * Dismissible, never reappears in the same session. Follows DESIGN.md
 * §9: "non-intrusive donation prompt, minimal, elegant, after first
 * RT60 analysis, dismissible, never blocks workflow."
 */
export default function DonationBanner() {
  const visible = useUIStore((s) => s.isDonationBannerVisible);
  const dismiss = useUIStore((s) => s.dismissDonationBanner);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          style={{
            position: 'relative',
            margin: 'var(--space-md)',
            padding: 'var(--space-lg)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-mid)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
          }}
        >
          {/* Dismiss */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss donation banner"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--text-hint)',
              transition: 'color 0.15s ease-out',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-hint)';
            }}
          >
            <Icon icon={X} size={14} />
          </button>

          {/* Heart icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-md)',
                background: 'rgba(112, 148, 36, 0.12)',
                border: '1px solid rgba(112, 148, 36, 0.3)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon icon={Heart} size={14} color="var(--accent-green)" />
            </div>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 12,
                color: 'var(--text-primary)',
              }}
            >
              StudioShell is free forever
            </span>
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 11,
              lineHeight: 1.5,
              color: 'var(--text-muted)',
            }}
          >
            If it saved you time planning your studio acoustics, consider
            supporting the project so it stays free and keeps improving.
          </p>

          <Button
            variant="primary"
            size="sm"
            icon={<Icon icon={Coffee} size={14} />}
            onClick={() => {
              track('donation_clicked', { location: 'banner' });
              window.open(KOFI_URL, '_blank', 'noopener');
            }}
          >
            Support on Ko-fi ↗
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
